import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Button, CircularProgress, Divider, useTheme } from '@mui/material';
import { darken } from 'polished';
import styled, { css } from 'styled-components';

import Export from '@/assets/icons/export.svg';
import { CsvUploadResponse } from '@/features/canvas/api/import';
import { CsvLinkage } from '@/features/canvas/components/ImportAccordionContent/CsvLinkage';
import { Linkage } from '@/features/canvas/components/ImportAccordionContent/Linkage';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useImportInitiativeContext } from '@/features/canvas/contexts/import-initiative-context';
import { exportRoadmapToCsv } from '@/features/roadmaps/api';
import { useShowToast } from '@/hooks/useShowToast';

import { CsvUploadButton } from './CsvUploadButton';

const ImportContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1rem;
`;

const ImportAccordionContent = ({ isExpanded }: { isExpanded: boolean }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showToast } = useShowToast();
  const { roadmap } = useEditorContext();
  const { isInitiativeImportRunning } = useImportInitiativeContext();

  const [serverCsvData, setServerCsvData] = useState<CsvUploadResponse | null>(null);

  const downloadBlob = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[-:T]/g, '').substring(0, 14);
    const cleanTitle = (roadmap.title || 'untitled').replace(/[^A-Za-z0-9_-]/g, '_');
    a.download = `${timestamp}_${cleanTitle}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const { mutateAsync: handleExportCsv, isLoading: isExportingCsv } = useMutation(
    () => exportRoadmapToCsv(roadmap.id!),
    {
      onSuccess: blob => {
        downloadBlob(blob);
        showToast('success', t('editor.header.export_csv_success'));
      },
      onError: () => {
        showToast('error', t('editor.header.export_csv_error'));
      },
    }
  );

  const handleUploadComplete = (data: CsvUploadResponse) => {
    console.log('CSV Upload Complete:', data);
    showToast('success', t('editor.sidebar.data_tab.csv_upload_success'));
    setServerCsvData(data);
  };

  return (
    <ContentWrapper $isExpanded={isExpanded}>
      {isInitiativeImportRunning && (
        <LoaderWrapper>
          <StyledLoader />
        </LoaderWrapper>
      )}

      <SectionContainer>
        <SectionHeader>{t('editor.sidebar.import_section')}</SectionHeader>
        <Divider variant="fullWidth" sx={{ borderColor: theme.palette.brand.backgroundDialog }} />
        <ImportContentWrapper>
          <CsvUploadButton onUploadComplete={handleUploadComplete} />
          <CsvLinkage
            serverCsvData={serverCsvData}
            onClearServerData={() => setServerCsvData(null)}
          />
          <Linkage />
        </ImportContentWrapper>
      </SectionContainer>

      <SectionContainer>
        <SectionHeader>{t('editor.sidebar.export')}</SectionHeader>
        <Divider variant="fullWidth" sx={{ borderColor: theme.palette.brand.backgroundDialog }} />
        <ExportButtonWrapper>
          <ExportButton
            type="button"
            startIcon={
              isExportingCsv ? <CircularProgress size={16} /> : <ButtonIcon src={Export} />
            }
            onClick={() => handleExportCsv()}
            disabled={isExportingCsv}
          >
            {t('editor.header.export_csv')}
          </ExportButton>
        </ExportButtonWrapper>
      </SectionContainer>
    </ContentWrapper>
  );
};

export { ImportAccordionContent };

const ContentWrapper = styled.div<{ $isExpanded: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  padding: 4rem 1rem 1.5rem;
  height: 0;
  overflow: hidden;
  transition: height 0.3s ease-in-out;
  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      overflow-y: auto;
      height: 100%;
      animation: hide-scroll 0.3s backwards;
    `}
  @keyframes hide-scroll {
    from,
    to {
      overflow: hidden;
    }
  }
`;

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 1rem 0;
  font-weight: 600;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const ExportButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
`;

const ExportButton = styled(Button)`
  display: flex;
  align-items: center;
  border-radius: 2rem;
  padding: 0.25rem 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  font-size: 1.75rem;
  font-weight: 600;
  text-transform: none;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  background-color: ${({ theme }) => theme.palette.brand.white};
  gap: 0 !important;
  justify-content: center !important;

  :hover {
    background-color: ${({ theme }) => darken(0.1, theme.palette.brand.white)};
  }

  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.img`
  width: 1.5rem;
  height: 1.5rem;
`;
